import { useState, type Ref } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useInventoryParts, type PartListItem } from '@/hooks/useInventory';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuthStore } from '@/stores/authStore';

/** Free text stays editable; catalog results include server-side keyword matches. */
export function PartCatalogInput({
  value,
  onChange,
  onSelect,
  inputRef,
  placeholder,
  helperText,
}: {
  value: string;
  onChange: (value: string) => void;
  onSelect: (part: PartListItem) => void;
  inputRef?: Ref<HTMLInputElement>;
  /** Overrides the work-order wording where free text is not a valid choice (#3845). */
  placeholder?: string | undefined;
  helperText?: string | undefined;
}) {
  const { t } = useTranslation('workorders');
  const [open, setOpen] = useState(false);
  const label = placeholder ?? t('detail.partName');
  const query = useDebounce(value.trim(), 250);
  const canReadInventory = useAuthStore((state) => state.hasPermission('inventory.read'));
  const parts = useInventoryParts(
    { q: query, limit: 20 },
    {
      enabled: canReadInventory && open && query.length > 0,
    }
  );
  const options =
    query === value.trim() && query.length > 0
      ? (parts.data?.pages.flatMap((page) => page.data) ?? [])
      : [];

  return (
    <Autocomplete
      freeSolo
      size="small"
      sx={{ flex: 1, minWidth: 120 }}
      value={value}
      inputValue={value}
      open={canReadInventory && open && value.trim().length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
      loading={parts.isFetching}
      onInputChange={(_, input, reason) => {
        if (reason === 'input' || reason === 'clear') onChange(input);
      }}
      onChange={(_, part) => {
        if (typeof part === 'string') onChange(part);
        else if (part) onSelect(part);
        else onChange('');
      }}
      renderOption={(props, part) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            {part.name} — {part.sku}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={label}
          inputRef={inputRef}
          helperText={open && canReadInventory ? (helperText ?? t('detail.partSearchHint')) : undefined}
          slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': label } }}
        />
      )}
    />
  );
}
